import React, { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { TextField, Typography, IconButton, Tooltip, InputAdornment } from '@mui/material';
import Delete from '@mui/icons-material/Delete';
import AutocompleteInput from '../components/AutocompleteInput';

const usePurchaseTable = ({
    control,
    errors,
    isEditMode,
    hasLimit,
    axiosInstance,
    selectedSupplier,
    purchaseData,
    setPurchaseData,
    calculateSellingPriceIncludingTax,
    calculateItemSubtotal,
    calculateTotalAmount,
    formatCurrency,
    handleItemDelete,
}) => {

    const updateItem = (index, field, value) => {
        if (!purchaseData?.items?.[index]) return;

        const updatedItems = [...(purchaseData.items ?? [])];
        const updatedItem = { ...updatedItems[index] };

        // 値反映
        updatedItem[field] = value;

        // 数値に正規化（←超重要）
        const price = Number(updatedItem.purchase_price ?? 0);
        const taxRate = Number(updatedItem.tax_rate ?? 0);
        const quantity = Number(updatedItem.quantity ?? 0);

        // 販売単価（円）
        updatedItem.purchase_price = price;

        // 税込単価
        updatedItem.purchase_price_including_tax =
            calculateSellingPriceIncludingTax(updatedItem.purchase_price, taxRate);

        // サブトータル
        const { subtotal, subtotal_including_tax } =
            calculateItemSubtotal({
                price: updatedItem.purchase_price,
                quantity,
                tax_rate: updatedItem.tax_rate,
            });
        updatedItem.subtotal = subtotal;
        updatedItem.subtotal_including_tax = subtotal_including_tax;

        updatedItems[index] = updatedItem;

        // 合計
        const { total_amount, total_amount_including_tax } =
            calculateTotalAmount(updatedItems);

        setPurchaseData(prev => ({
            ...prev,
            items: updatedItems,
            total_amount,
            total_amount_including_tax,
        }));
    };
    const handleItemChange = (e, index, selectedProduct = null) => {
        const { name, value } = e.target;

        const updatedItems = [...purchaseData.items];

        let updatedItem = {
            ...updatedItems[index],
            [name]: value,
        };

        // SKU選択時
        if (selectedProduct) {
            const price = selectedProduct.price ?? 0;
            const purchasePrice = selectedProduct.purchase_price ?? 0;
            const taxRate = selectedProduct.tax_rate ?? 0;

            updatedItem = {
                ...updatedItem,
                variant_id: selectedProduct.variant_id ?? '',
                product_id: selectedProduct.product_id ?? '',
                sku: selectedProduct.sku ?? '',
                product_name: e.target.sku_product_name ?? selectedProduct.product_name ?? '',
                option_combination: selectedProduct.option_combination ?? '',
                price,
                purchase_price: purchasePrice,
                tax_rate: taxRate,
                purchase_price_including_tax: calculateSellingPriceIncludingTax(purchasePrice, taxRate),
            };
        }

        // 1アイテムごとのサブトータルを計算
        updatedItem = {
            ...updatedItem,
            ...calculateItemSubtotal({
                price: updatedItem.purchase_price,
                quantity: updatedItem.quantity,
                tax_rate: updatedItem.tax_rate,
            }),
        };
        updatedItems[index] = updatedItem;

        // 全体合計を計算
        const { total_amount, total_amount_including_tax } = calculateTotalAmount(updatedItems);

        setPurchaseData({
            ...purchaseData,
            items: updatedItems,
            total_amount,
            total_amount_including_tax,
        });
    };
    const columns = useMemo(() => [
        {
            label: '品番',
            accessor: 'sku',
            render: (item, index) => (
                <>
                    <Controller
                        name={`items.${index}.sku`}
                        control={control}
                        rules={{
                            required: '品番は必須です',
                            maxLength: {
                                value: 100,
                                message: '100文字以内で入力してください',
                            },
                        }}
                        render={({ field }) => (
                            <AutocompleteInput
                                hasLimit={hasLimit}
                                isEditMode={isEditMode}
                                value={field.value || ''}
                                index={index}
                                fetchSuggestions={
                                    selectedSupplier?.supplier_id
                                        ? async (keyword) => {
                                            try {
                                                const res = await axiosInstance.post('/product/purchase_sku_suggest', {
                                                    keyword,
                                                    supplier_id: selectedSupplier.supplier_id
                                                });
                                                return res.data;
                                            } catch (err) {
                                                console.error(err);
                                                return [];
                                            }
                                        }
                                        : async () => []
                                }
                                onChange={(e, idx, selectedProduct) => {
                                    const value =
                                        selectedProduct?.sku ||
                                        e?.target?.value ||
                                        '';

                                    field.onChange(value);
                                    handleItemChange(e, idx, selectedProduct);
                                }}
                                error={!!errors.items?.[index]?.sku}
                                helperText={errors.items?.[index]?.sku?.message}
                            />
                        )}
                    />
                    {item.supplier_product_code && (
                        <Typography
                            variant='caption'
                            sx={{ color: 'text.secondary' }}
                        >
                            （仕入先商品コード {item.supplier_product_code}）
                        </Typography>
                    )}
                </>
            ),
            width: '200px',
        },
        {
            label: '商品名',
            accessor: 'product_name',
            width: '250px',
            render: (item) => (
                <>
                    <Typography sx={{ maxWidth: 400 }}>{item.product_name}</Typography>
                    {item.supplier_product_name && (
                        <Typography
                            variant='caption'
                            sx={{ color: 'text.secondary' }}
                        >
                            （仕入先商品名 {item.supplier_product_name}）
                        </Typography>
                    )}
                </>
            )
        },
        {
            label: '数量',
            accessor: 'quantity',
            render: (item, index) => (
                <Controller
                    name={`items.${index}.quantity`}
                    control={control}
                    rules={{
                        validate: value => {
                            if (value === '' || value == null) return '数量は必須です';
                            if (!/^\d+$/.test(String(value))) return '半角数字で入力してください';
                            if (parseInt(value, 10) < 1) return '1以上の数で入力してください';
                            if (parseInt(value, 10) > 999999) return '6桁以内で入力してください';
                            return true;
                        },
                    }}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            type="text"
                            disabled={!isEditMode}
                            error={!!errors.items?.[index]?.quantity}
                            helperText={errors.items?.[index]?.quantity?.message}
                            sx={{ width: 120 }}
                            inputProps={{
                                maxLength: 6,
                                inputMode: 'numeric',
                            }}
                            onChange={(e) => {
                                const raw = e.target.value;
                                field.onChange(raw);
                                // 整数チェックして変換
                                if (!/^\d+$/.test(raw)) return;
                                updateItem(index, 'quantity', parseInt(raw, 10));
                            }}
                        />
                    )}
                />
            ),
            minWidth: '90px',
        },
        {
            label: '販売単価',
            accessor: 'purchase_price',
            render: (item) => (
                <>
                    <Typography>{formatCurrency(item.purchase_price ?? 0)}</Typography>
                    <Typography variant='caption'>税込{formatCurrency(item.purchase_price_including_tax ?? 0)}</Typography>
                </>
            ),
            minWidth: '100px',
        },
        {
            label: '消費税',
            accessor: 'tax_rate',
            render: (item, index) => (
                <TextField
                    value={item.tax_rate ?? ''}
                    type="text"
                    disabled={!isEditMode}
                    error={!!errors.items?.[index]?.tax_rate}
                    helperText={errors.items?.[index]?.tax_rate?.message}
                    sx={{ width: 80 }}
                    inputProps={{
                        maxLength: 3,
                        inputMode: 'numeric',
                    }}
                    onChange={(e) => {
                        const raw = e.target.value;

                        if (
                            /^\d*$/.test(raw) &&
                            (raw === '' || parseInt(raw, 10) <= 100)
                        ) {
                            updateItem(
                                index,
                                'tax_rate',
                                raw === '' ? 0 : parseInt(raw, 10)
                            );
                        }
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                        ),
                    }}
                />
            ),
        },
        {
            label: '金額',
            accessor: 'subtotal',
            render: (item) => (
                <>
                    <Typography>{formatCurrency(item.subtotal ?? 0)}</Typography>
                    <Typography variant='caption'>税込{formatCurrency(item.subtotal_including_tax ?? 0)}</Typography>
                </>
            ),
            minWidth: '120px',
        },
        ...(
            purchaseData?.status_id == null ||
                ['1'].includes(purchaseData?.status_id)
                ? [
                    {
                        label: '',
                        render: (item, index) => (
                            <Tooltip title="削除">
                                <IconButton
                                    color="error"
                                    size="small"
                                    onClick={() => handleItemDelete(index, item.item_id)}
                                >
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        ),
                    },
                ]
                : []
        ),
    ], [
        purchaseData,
        isEditMode,
        errors,
        control,
        hasLimit,
    ]);

    return {
        columns,
        updateItem,
        handleItemChange,
    };
};

export default usePurchaseTable;